import { createContext, useContext, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

type Shown<T> = { key: string; step: T; index: number; back: boolean };

const StepLeaving = createContext(false);

/**
 * True while the step is sliding out. A leaving step should drop its modals:
 * they'd slide out with it, squeezed into its box.
 */
export function useStepLeaving() {
  return useContext(StepLeaving);
}

/**
 * Slides from one step to the next: the old step slides out while the new one
 * slides in on top of it (from the right going forward, from the left going
 * back). Takes the step as data and renders it with `children`, so the leaving
 * step keeps rendering (and stays mounted) until its exit animation ends.
 * Needs `GuideKeyframes` on the page.
 */
export function StepTransition<T>({
  step,
  stepKey,
  index,
  children: render,
}: {
  step: T;
  // a new key is a new step
  stepKey: string;
  // position in the flow; a lower one than before slides back
  index: number;
  children: (step: T) => ReactNode;
}) {
  const [shown, setShown] = useState<Shown<T>>({
    key: stepKey,
    step,
    index,
    back: false,
  });
  const [leaving, setLeaving] = useState<Shown<T> | null>(null);
  // The step finished sliding in: its animation is dropped, because an element
  // with a transform animation (even one filled at `none`) is the containing
  // block of its `position: fixed` children, so a step's modals would only
  // cover the step instead of the screen.
  const [settled, setSettled] = useState(false);
  // a new step: the shown one starts leaving (state from props, during render)
  if (shown.key !== stepKey) {
    const back = index < shown.index;
    setLeaving({ ...shown, back });
    setShown({ key: stepKey, step, index, back });
    setSettled(false);
  } else if (shown.step !== step) {
    // same step, new data: it leaves with what it showed last
    setShown({ ...shown, step });
  }

  const items = [
    ...(leaving && leaving.key !== stepKey ? [{ ...leaving, isLeaving: true }] : []),
    { key: stepKey, step, isLeaving: false },
  ];
  const back = shown.key === stepKey ? shown.back : index < shown.index;

  return (
    // the slide must not scroll the page sideways
    <div className="grid flex-1 grid-cols-1 overflow-x-clip">
      {items.map((item) => (
        <div
          key={item.key}
          className={cn(
            "col-start-1 row-start-1 flex min-w-0 flex-col",
            item.isLeaving && "pointer-events-none",
          )}
          style={{
            animation: item.isLeaving
              ? `${back ? "flow-exit-back" : "flow-exit"} 250ms ease-in forwards`
              : settled
                ? undefined
                : `${back ? "flow-enter-back" : "flow-enter"} 400ms ease-out 100ms both`,
          }}
          onAnimationEnd={(event) => {
            // children's own animations bubble up here too
            if (event.target !== event.currentTarget) return;
            if (item.isLeaving && event.animationName.startsWith("flow-exit")) {
              setLeaving(null);
            }
            if (!item.isLeaving && event.animationName.startsWith("flow-enter")) {
              setSettled(true);
            }
          }}
        >
          <StepLeaving.Provider value={item.isLeaving}>
            {render(item.step)}
          </StepLeaving.Provider>
        </div>
      ))}
    </div>
  );
}
